import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface User {
  id: string;
  email: string;
  name: string;
  avatar: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Convert Supabase user to our User type
const formatUser = (supabaseUser: SupabaseUser): User => {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || "",
    name:
      supabaseUser.user_metadata?.full_name ||
      supabaseUser.user_metadata?.name ||
      supabaseUser.email?.split("@")[0] ||
      "User",
    avatar:
      supabaseUser.user_metadata?.avatar_url ||
      supabaseUser.user_metadata?.picture ||
      `https://api.dicebear.com/7.x/avataaars/svg?seed=${supabaseUser.id}`,
  };
};

interface AuthProviderProps {
  children: ReactNode;
}

// Dev-only escape hatch for E2E tests: when running under `pnpm vite` with
// VITE_E2E_AUTH=true (set by playwright.config.ts), and a serialized user has
// been seeded into localStorage (via loginAsTestUser() in e2e/auth.ts), skip
// the Supabase round-trip and return that user immediately. import.meta.env.DEV
// is statically false in production builds, so this whole branch is dead-code
// eliminated from the production bundle.
const readE2EMockUser = (): User | null => {
  if (!import.meta.env.DEV) return null;
  if (import.meta.env.VITE_E2E_AUTH !== "true") return null;
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("__judi_e2e_user__");
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const mockUser = readE2EMockUser();
    if (mockUser) {
      setUser(mockUser);
      setIsLoading(false);
      return;
    }

    // Check for existing session on mount
    const getSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(formatUser(session.user));
        }
      } catch (err) {
        console.error("Error reading session:", err);
      } finally {
        setIsLoading(false);
      }
    };

    void getSession();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event: string, session: Session | null) => {
        if (session?.user) {
          setUser(formatUser(session.user));
          Sentry.setUser({ id: session.user.id, email: session.user.email });
        } else {
          setUser(null);
          Sentry.setUser(null);
        }
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setIsLoading(true);
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Google sign-in initiated",
      level: "info",
    });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.href,
      },
    });
    if (error) {
      console.error("Error signing in with Google:", error.message);
      toast.error("Failed to sign in. Please try again.");
      setIsLoading(false);
    }
    // Note: isLoading will be set to false by the onAuthStateChange listener
  };

  const signOut = async () => {
    setIsLoading(true);
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Sign-out initiated",
      level: "info",
    });
    const { error } = await supabase.auth.signOut();
    if (error && error.message !== "Auth session missing!") {
      console.error("Error signing out:", error.message);
      toast.error("Failed to sign out. Please try again.");
    }
    setUser(null);
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
